/**
 * SSE client for POST /api/broad/resolve/stream (Broad streaming, Phase 3, 2026-08-17).
 *
 * The streamed twin of resolveBroad (./broad.ts). Where resolveBroad blocks for
 * ~36s and returns the whole verified list at once, this yields the category as
 * it builds: the ranked candidates arrive immediately (page fills at ~6s), then
 * each one's in-stock verification lands as it completes.
 *
 * Same wire mechanics as openSearchStream (./search-stream.ts): the browser's
 * native EventSource is GET-only, so we fetch() a POST and parse the SSE frames
 * off the response body's ReadableStream ourselves. Each frame is
 *
 *   event: <name>\n
 *   data: <json blob>\n
 *   \n
 *
 * Lines starting with ":" are keep-alive comments, surfaced as {kind:"keepalive"}
 * so a consumer's idle watchdog can reset. Callers consume via `for await`.
 *
 * Event order for a broad category (see api services/broad_search/stream.py):
 *   mode(broad) -> picks(all candidates) -> verified(one per candidate) -> done
 * For a non-category query a single mode(exact|ask|out_of_scope) then the stream
 * closes, and the caller falls through to today's flow.
 */

import { searchIdentityHeaders } from "@/lib/anon-session";
import { bearerHeaders } from "@/lib/auth-bearer";
import { proofHeaders } from "@/lib/human-proof";

import type { BroadProduct } from "./broad";

/**
 * The stream's classification. NOTE: a category is "broad" here (not the
 * blocking resolve's "picks"), deliberately so the mode value never collides
 * with the `picks` event name. "exact" / "ask" / "out_of_scope" match resolve.
 */
export type BroadStreamMode = "broad" | "exact" | "ask" | "out_of_scope";

export type BroadStreamEvent =
  | {
      kind: "mode";
      mode: BroadStreamMode;
      /** broad: e.g. "Best office chair" */
      heading: string | null;
      /** broad: the pSEO /c/list slug the confirmed picks save under */
      slug: string | null;
      /** exact: the specific product the parser named */
      product_label: string | null;
      /** out_of_scope: why we don't cover it */
      reason: string | null;
    }
  // All ranked candidates, up front - the page fills now, before verification.
  | { kind: "picks"; slug: string | null; products: BroadProduct[] }
  // One per candidate as its in-stock check finishes; `index` is its position in
  // the `picks` array. in_stock false -> the card is removed from the list.
  | { kind: "verified"; index: number; in_stock: boolean; shop_count: number }
  // Terminal: the confirmed, ranked, saved set. `kept_indices` are the `picks`
  // positions to keep (buyable, top 15), in final display order.
  | { kind: "done"; kept_indices: number[]; spent_usd: string }
  | { kind: "error"; error: string; [field: string]: unknown }
  // A server keepalive (SSE comment): liveness only, no data.
  | { kind: "keepalive" };

export class BroadStreamError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function apiBaseUrl(): string {
  if (typeof window === "undefined") {
    return process.env.API_INTERNAL_URL ?? process.env.API_URL ?? "http://localhost:8000";
  }
  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
}

/**
 * Open POST /api/broad/resolve/stream and yield parsed SSE events until the
 * server closes the stream.
 *
 * Throws `BroadStreamError` if the HTTP status is non-2xx (the stream never
 * starts - notably a 404 when the mio_broad_stream_enabled flag is off, which
 * the caller treats as "fall back to the blocking resolveBroad") or if the
 * response body is missing.
 *
 * `signal` is an AbortSignal the caller can use to cancel the stream (e.g. on
 * unmount or when the shopper navigates away); cancelling tears down the fetch,
 * whose server-side `finally` cancels the outstanding cluster fetches.
 *
 * `userId` (Clerk id when signed in) rides along as identity headers, mirroring
 * openSearchStream, so the per-IP gate and any future wall counting see the
 * same identity the live search does.
 */
export async function* openBroadStream(
  query: string,
  countryCode = "AU",
  signal?: AbortSignal,
  userId?: string | null,
): AsyncGenerator<BroadStreamEvent, void, void> {
  const url = `${apiBaseUrl()}/api/broad/resolve/stream`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    ...searchIdentityHeaders(userId),
    ...(await proofHeaders()),
    ...(await bearerHeaders()),
  };
  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify({ query, country_code: countryCode }),
    signal,
    cache: "no-store",
  });

  if (!response.ok || !response.body) {
    throw new BroadStreamError(
      response.status,
      `POST /api/broad/resolve/stream returned ${response.status}`,
    );
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();

  // Cross-chunk buffer: SSE frames can straddle chunk boundaries.
  let buffer = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;
      // Each frame is terminated by a blank line ("\n\n"). Emit every complete
      // frame; leave the trailing partial in the buffer.
      let boundary: number;
      while ((boundary = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseBroadSseEvent(raw);
        if (parsed !== null) {
          yield parsed;
        }
      }
    }
  } finally {
    // Best-effort teardown if the consumer exits the for-await early (abort).
    try {
      await reader.cancel();
    } catch {
      // Reader already cancelled; ignore.
    }
  }
}

function parseBroadSseEvent(raw: string): BroadStreamEvent | null {
  if (!raw.trim()) return null;
  if (raw.startsWith(":")) return { kind: "keepalive" };

  let eventName = "";
  let dataLine = "";
  for (const line of raw.split("\n")) {
    if (line.startsWith("event: ")) {
      eventName = line.slice(7).trim();
    } else if (line.startsWith("data: ")) {
      // SSE allows multi-line data via repeated `data:` lines; the endpoint
      // emits one line per event, but be defensive.
      dataLine += (dataLine ? "\n" : "") + line.slice(6);
    }
  }
  if (!eventName || !dataLine) return null;

  let payload: unknown;
  try {
    payload = JSON.parse(dataLine);
  } catch {
    return null;
  }

  switch (eventName) {
    case "mode": {
      const p = payload as {
        mode: BroadStreamMode;
        heading?: string | null;
        slug?: string | null;
        product_label?: string | null;
        reason?: string | null;
      };
      return {
        kind: "mode",
        mode: p.mode,
        heading: p.heading ?? null,
        slug: p.slug ?? null,
        product_label: p.product_label ?? null,
        reason: p.reason ?? null,
      };
    }
    case "picks": {
      const p = payload as { slug?: string | null; products?: BroadProduct[] };
      return {
        kind: "picks",
        slug: p.slug ?? null,
        products: Array.isArray(p.products) ? p.products : [],
      };
    }
    case "verified": {
      const p = payload as {
        index: number;
        in_stock?: boolean;
        shop_count?: number;
      };
      return {
        kind: "verified",
        index: p.index,
        in_stock: Boolean(p.in_stock),
        shop_count: p.shop_count ?? 0,
      };
    }
    case "done": {
      const p = payload as { kept_indices?: number[]; spent_usd?: string };
      return {
        kind: "done",
        kept_indices: Array.isArray(p.kept_indices) ? p.kept_indices : [],
        spent_usd: p.spent_usd ?? "0",
      };
    }
    case "error":
      return { kind: "error", ...(payload as { error: string }) };
    default:
      return null;
  }
}
